import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Card, Container, Badge, Form, Alert, ButtonGroup } from 'react-bootstrap';
import api from '../../api/axios';

const STATUS_LABELS = {
    pending: { variant: 'secondary', label: '대기중' },
    invited: { variant: 'info', label: '초대됨' },
    registered: { variant: 'success', label: '가입완료' },
    rejected: { variant: 'danger', label: '거절' },
};

const EMPTY_FORM = { shopName: '', ownerName: '', phone: '', email: '' };

const WaitlistPage = () => {
    const [waitlist, setWaitlist] = useState([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [addForm, setAddForm] = useState(EMPTY_FORM);
    const [adding, setAdding] = useState(false);

    const fetchWaitlist = useCallback(async () => {
        setLoading(true);
        try {
            const url = statusFilter ? `/superadmin/waitlist?status=${statusFilter}` : '/superadmin/waitlist';
            const res = await api.get(url);
            setWaitlist(res.data.data || []);
        } catch (err) {
            setError(err.response?.data?.errors?.[0]?.msg || '대기자 목록 조회 실패');
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => {
        fetchWaitlist();
    }, [fetchWaitlist]);

    const handleInvite = async (id) => {
        setError('');
        setSuccess('');
        try {
            const res = await api.post(`/superadmin/waitlist/${id}/invite`);
            const code = res.data.data?.invite_code;
            const link = `${window.location.origin}/signup?code=${code}`;
            try {
                await navigator.clipboard.writeText(link);
                setSuccess(`초대 링크가 복사되었습니다: ${link}`);
            } catch {
                setSuccess(`초대 링크: ${link}`);
            }
            await fetchWaitlist();
        } catch (err) {
            setError(err.response?.data?.errors?.[0]?.msg || '초대 발송 실패');
        }
    };

    const handleEarlyBirdToggle = async (id, currentValue) => {
        try {
            await api.put(`/superadmin/waitlist/${id}/early-bird`, {
                is_early_bird: !currentValue,
            });
            await fetchWaitlist();
        } catch (err) {
            setError(err.response?.data?.errors?.[0]?.msg || '변경 실패');
        }
    };

    const handleReject = async (id) => {
        if (!window.confirm('정말 거절하시겠습니까?')) return;
        try {
            await api.delete(`/superadmin/waitlist/${id}`);
            await fetchWaitlist();
        } catch (err) {
            setError(err.response?.data?.errors?.[0]?.msg || '거절 실패');
        }
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setAdding(true);
        try {
            await api.post('/superadmin/waitlist', addForm);
            setSuccess('대기자가 추가되었습니다.');
            setAddForm(EMPTY_FORM);
            setShowAddForm(false);
            await fetchWaitlist();
        } catch (err) {
            setError(err.response?.data?.errors?.[0]?.msg || '추가 실패');
        } finally {
            setAdding(false);
        }
    };

    const handleCopyLink = async (code) => {
        const link = `${window.location.origin}/signup?code=${code}`;
        try {
            await navigator.clipboard.writeText(link);
            setSuccess(`링크 복사됨: ${link}`);
        } catch {
            setSuccess(`링크: ${link}`);
        }
    };

    return (
        <Container className="py-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                    <h2 className="mb-0">사전 신청 관리</h2>
                    <p className="text-muted mb-0">사전 신청자에게 초대 코드를 발송하고 가입 상태를 관리합니다.</p>
                </div>
                <Button variant="primary" size="sm" onClick={() => setShowAddForm(!showAddForm)}>
                    {showAddForm ? '닫기' : '+ 직접 추가'}
                </Button>
            </div>

            {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
            {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

            {showAddForm && (
                <Card className="mb-3">
                    <Card.Body>
                        <Form onSubmit={handleAdd}>
                            <div className="d-flex gap-2 flex-wrap">
                                <Form.Control size="sm" placeholder="매장명 *" required style={{ maxWidth: 160 }}
                                    value={addForm.shopName} onChange={e => setAddForm(f => ({ ...f, shopName: e.target.value }))} />
                                <Form.Control size="sm" placeholder="대표자명 *" required style={{ maxWidth: 120 }}
                                    value={addForm.ownerName} onChange={e => setAddForm(f => ({ ...f, ownerName: e.target.value }))} />
                                <Form.Control size="sm" placeholder="연락처 *" required style={{ maxWidth: 140 }}
                                    value={addForm.phone} onChange={e => setAddForm(f => ({ ...f, phone: e.target.value }))} />
                                <Form.Control size="sm" placeholder="이메일" type="email" style={{ maxWidth: 180 }}
                                    value={addForm.email} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))} />
                                <Button type="submit" size="sm" variant="success" disabled={adding}>
                                    {adding ? '추가 중...' : '추가'}
                                </Button>
                            </div>
                        </Form>
                    </Card.Body>
                </Card>
            )}

            <Card className="mb-3">
                <Card.Body>
                    <Form.Group>
                        <Form.Label>상태 필터</Form.Label>
                        <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ maxWidth: 200 }}>
                            <option value="">전체</option>
                            <option value="pending">대기중</option>
                            <option value="invited">초대됨</option>
                            <option value="registered">가입완료</option>
                            <option value="rejected">거절</option>
                        </Form.Select>
                    </Form.Group>
                </Card.Body>
            </Card>

            <Card>
                <Card.Body>
                    {loading ? (
                        <p>로딩 중...</p>
                    ) : waitlist.length === 0 ? (
                        <p className="text-muted">대기자가 없습니다.</p>
                    ) : (
                        <Table responsive hover size="sm">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>매장명</th>
                                    <th>대표자</th>
                                    <th>연락처</th>
                                    <th>이메일</th>
                                    <th>상태</th>
                                    <th>Early Bird</th>
                                    <th>신청일</th>
                                    <th>액션</th>
                                </tr>
                            </thead>
                            <tbody>
                                {waitlist.map((w) => {
                                    const statusInfo = STATUS_LABELS[w.status] || { variant: 'secondary', label: w.status };
                                    return (
                                        <tr key={w.id}>
                                            <td>{w.id}</td>
                                            <td>{w.shopName}</td>
                                            <td>{w.ownerName}</td>
                                            <td>{w.phone}</td>
                                            <td>{w.email || '-'}</td>
                                            <td><Badge bg={statusInfo.variant}>{statusInfo.label}</Badge></td>
                                            <td>
                                                <Form.Check
                                                    type="switch"
                                                    checked={!!w.is_early_bird}
                                                    onChange={() => handleEarlyBirdToggle(w.id, !!w.is_early_bird)}
                                                    disabled={w.status === 'registered'}
                                                />
                                            </td>
                                            <td className="small">{w.created_at?.slice(0, 10)}</td>
                                            <td>
                                                <ButtonGroup size="sm">
                                                    {(w.status === 'pending' || w.status === 'invited') && (
                                                        <Button variant="primary" onClick={() => handleInvite(w.id)}>
                                                            {w.status === 'invited' ? '재발송' : '초대'}
                                                        </Button>
                                                    )}
                                                    {w.status === 'invited' && w.invite_code && (
                                                        <Button variant="outline-secondary" onClick={() => handleCopyLink(w.invite_code)}>
                                                            링크
                                                        </Button>
                                                    )}
                                                    {w.status !== 'rejected' && w.status !== 'registered' && (
                                                        <Button variant="outline-danger" onClick={() => handleReject(w.id)}>
                                                            거절
                                                        </Button>
                                                    )}
                                                </ButtonGroup>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>
        </Container>
    );
};

export default WaitlistPage;
